/**
 * @file ContactEditModal.js
 * @version 1.20.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-18
 * @modified 2026-10-21
 * @description Modal for editing contact details with support for dual addresses, customer/supplier flags,
 *              contact groups with E/EP prefix logic, and Xero sync. onSave(createdInXero, contact)
 *              passes the persisted record back so create-then-link callers can map it.
 *              Creating a contact supports a group multiselect and prompts for an E prefix +
 *              email when the Email Invoices group is chosen. The group
 *              multiselect is editable on BOTH create and edit — a stale note
 *              here used to say otherwise, and it was believed.
 *              1.20.0 (2026-10-08, Cam): the till drafts-for-pricing tick after
 *              the VAT number — local only, a customer's, saved only once the
 *              contact's detail has loaded.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactEditModal.css';
import Dropdown from './Dropdown';
import Input from './Input';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import ConflictResolutionModal from './ConflictResolutionModal';
import MultiSelectDropdown from './MultiSelectDropdown';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';

function ContactEditModal({ contact, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [suggestedAccountNo, setSuggestedAccountNo] = useState('');
  const [groupSetupPrompt, setGroupSetupPrompt] = useState(null); // { needsEmail, needsPrefix } | null
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]); // create-only group multiselect
  const [initialFormData, setInitialFormData] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [xeroWarning, setXeroWarning] = useState('');
  // A Nutri-Ag suggestion held open after a successful create: the contact is
  // saved either way, so this only decides whether it also gets linked.
  const [nutriAg, setNutriAg] = useState(null);
  const [nutriAgLinking, setNutriAgLinking] = useState(false);
  // People on the contact (Xero ContactPersons) + the primary person. Edited
  // locally and bundled into the form submit. Each person may carry a transient
  // `_editing` flag (row shown as inputs vs read-only).
  const [contactPersons, setContactPersons] = useState([]);
  // True once the contact's authoritative detail (groups + people + VAT) has
  // loaded, so we only send groups/VAT/primary-name on save when we actually
  // have them — never wiping groups or clearing VAT from a stale/partial prop.
  const [detailLoaded, setDetailLoaded] = useState(false);
  // Xero's own Customer / Supplier flags as the contact sync last saw them
  // (null = not seen yet). Xero sets these from the contact's invoices and
  // bills, and the sync keeps local OR Xero — so a tick Xero holds cannot be
  // removed here; the server refuses it too (XERO_HOLDS_CONTACT_TYPE).
  const [xeroTypeFlags, setXeroTypeFlags] = useState({ customer: null, supplier: null });
  // Saved here while Xero was not answering and not in Xero yet (or in Xero,
  // with a note that its people or groups did not go): { pending, note }.
  const [xeroPending, setXeroPending] = useState({ pending: false, note: null });

  // Slideout buttons
  useSlideOutButton();

  const emptyAddress = { attention: '', line1: '', line2: '', line3: '', line4: '', city: '', region: '', postalCode: '', country: '' };

  const [formData, setFormData] = useState({
    account_no: '',
    company_name: '',
    email: '',
    phone: '',
    mobile: '',
    phone_country_code: '',
    phone_area_code: '',
    mobile_country_code: '',
    mobile_area_code: '',
    rep_id: '',
    is_customer: true,
    is_supplier: false,
    contact_status: 'ACTIVE',
    credit_limit: '',
    credit_limit_notes: '',
    tax_number: '',
    deliveryAddress: { ...emptyAddress },
    invoiceAddress: { ...emptyAddress },
    notes: '',
    largest_vehicle_access: '',
    contracted: false,
    no_statement: false,
    till_draft_pricing: false
  });
  // Whether the till drafts-for-pricing tick can be saved yet (its column
  // comes with migrations/draft_invoices_for_pricing.sql) — known once the
  // contact's detail has loaded; on create the server simply ignores it.
  const [tillDraftReady, setTillDraftReady] = useState(true);

  // Fetch sales rep options from local database
  useEffect(() => {
    const fetchSalesReps = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contacts/sales-reps`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.reps) {
            setSalesRepOptions(data.reps);
          }
        }
      } catch (err) {
        console.error('Failed to fetch sales reps:', err);
      }
    };
    fetchSalesReps();
  }, []);

  // Load the contact's authoritative detail when editing — people, groups and
  // VAT — fetched fresh (rather than trusting the prop) so callers that pass a
  // lightweight contact (e.g. Search Contacts) still get everything, and so we
  // never save a partial group set. Falls back to the prop on failure.
  useEffect(() => {
    if (!contact || !contact.id) {
      // Creating: there is nobody to fetch, but the primary person is a row the
      // form still needs — its email is the Email field above, and its name is
      // Xero's top-level FirstName/LastName. Seeding it is what lets the People
      // section be filled in before the contact exists rather than after.
      setContactPersons([{
        first_name: '', last_name: '', email: '',
        include_in_emails: true, include_in_po_emails: false, is_primary: true, _editing: true
      }]);
      setDetailLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/contacts/${contact.id}`, { credentials: 'include' });
        const data = await res.json();
        if (cancelled || !data) return;
        if (Array.isArray(data.contactPersons)) setContactPersons(data.contactPersons);
        if (Array.isArray(data.groupsArray)) setSelectedGroups(data.groupsArray);
        setFormData(prev => ({
          ...prev,
          tax_number: data.tax_number || prev.tax_number || '',
          till_draft_pricing: data.till_draft_pricing === true
        }));
        setTillDraftReady(typeof data.till_draft_pricing === 'boolean');
        setXeroTypeFlags({
          customer: typeof data.xero_is_customer === 'boolean' ? data.xero_is_customer : null,
          supplier: typeof data.xero_is_supplier === 'boolean' ? data.xero_is_supplier : null
        });
        setXeroPending({
          pending: !data.xero_contact_id && String(data.contact_status || 'ACTIVE').toUpperCase() === 'ACTIVE',
          note: data.xero_push_error || null
        });
        setDetailLoaded(true);
      } catch (e) {
        if (!cancelled && Array.isArray(contact.contactPersons)) setContactPersons(contact.contactPersons);
      }
    })();
    return () => { cancelled = true; };
  }, [contact]);

  // Add / edit / remove non-primary people (Xero ContactPersons). All held
  // locally and bundled into the form submit — the save replaces the contact's
  // ContactPersons in Xero wholesale and reconciles the local rows. The primary
  // person (Xero's top-level email) is never edited here; it's the main Email
  // field above. Handlers key off the person's index in the full array.
  const updatePerson = (idx, field, value) => {
    setContactPersons((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };
  const setPersonEditing = (idx, editing) => {
    setContactPersons((prev) => prev.map((p, i) => (i === idx ? { ...p, _editing: editing } : p)));
  };
  const addPerson = () => {
    // New rows open in edit mode; existing rows start read-only with an edit btn.
    setContactPersons((prev) => [
      ...prev,
      { first_name: '', last_name: '', email: '', include_in_emails: false, include_in_po_emails: false, is_primary: false, _editing: true }
    ]);
  };
  const removePerson = (idx) => {
    setContactPersons((prev) => prev.filter((_, i) => i !== idx));
  };

  /*
   * A caller creating a contact for a known context can pre-pick its groups.
   *
   * Create Haulage Job passes the chosen supplier's delivery-accounts group, so
   * the new contact lands in the group that makes it selectable in that
   * supplier's customer dropdown — rather than being created correctly and then
   * being invisible to the screen that created it.
   *
   * On mount only, and create only: `contact.groupsArray` on an EDIT is the
   * contact's own saved groups, loaded below, and re-seeding from it would
   * undo whatever the user had just changed.
   */
  useEffect(() => {
    if (!isEditing && Array.isArray(contact?.groupsArray) && contact.groupsArray.length > 0) {
      setSelectedGroups(contact.groupsArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Available contact groups for the multiselect (both create and edit).
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contacts/available-groups`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.groups) setAvailableGroups(data.groups);
        }
      } catch (err) {
        console.error('Failed to fetch contact groups:', err);
      }
    };
    fetchGroups();
  }, []);

  // Fetch suggested account number when adding a new contact or editing one without an account number
  useEffect(() => {
    const fetchSuggestedAccountNo = async () => {
      // Skip if editing a contact that already has an account number
      if (contact && contact.account_no) return;

      try {
        const response = await fetch(`${API_URL}/api/contacts/next-account-number`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.suggestedAccountNo) {
            setSuggestedAccountNo(data.suggestedAccountNo);
          }
        }
      } catch (err) {
        console.error('Failed to fetch suggested account number:', err);
      }
    };
    fetchSuggestedAccountNo();
  }, [contact]);

  useEffect(() => {
    // Prevent background scrolling
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) {
      contentWrapper.classList.add('modal-open');
    }

    // Pre-populate form if editing
    if (contact) {
      // Determine customer/supplier status from contact_type
      const ct = contact.contact_type || '';
      const isCustomer = ct === 'CUSTOMER' || ct === 'BOTH' || !ct;
      const isSupplier = ct === 'SUPPLIER' || ct === 'BOTH';

      setFormData({
        account_no: contact.account_no || '',
        company_name: contact.company_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        phone_country_code: contact.phone_country_code || '',
        phone_area_code: contact.phone_area_code || '',
        mobile_country_code: contact.mobile_country_code || '',
        mobile_area_code: contact.mobile_area_code || '',
        rep_id: contact.rep_id || '',
        is_customer: isCustomer,
        is_supplier: isSupplier,
        contact_status: contact.contact_status || 'ACTIVE',
        credit_limit: contact.credit_limit || '',
        credit_limit_notes: contact.credit_limit_notes || '',
        deliveryAddress: {
          attention: contact.delivery_attention || '',
          line1: contact.delivery_line1 || '',
          line2: contact.delivery_line2 || '',
          line3: contact.delivery_line3 || '',
          line4: contact.delivery_line4 || '',
          city: contact.delivery_city || '',
          region: contact.delivery_region || '',
          postalCode: contact.delivery_postcode || '',
          country: contact.delivery_country || ''
        },
        invoiceAddress: {
          attention: contact.invoice_attention || '',
          line1: contact.invoice_line1 || '',
          line2: contact.invoice_line2 || '',
          line3: contact.invoice_line3 || '',
          line4: contact.invoice_line4 || '',
          city: contact.invoice_city || '',
          region: contact.invoice_region || '',
          postalCode: contact.invoice_postcode || '',
          country: contact.invoice_country || ''
        },
        notes: contact.notes || '',
        largest_vehicle_access: contact.largest_vehicle_access || '',
        contracted: contact.contracted || false,
        no_statement: contact.no_statement || false,
        till_draft_pricing: contact.till_draft_pricing === true
      });

      // Snapshot initial form state for conflict detection
      setInitialFormData({
        company_name: contact.company_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        phone_country_code: contact.phone_country_code || '',
        phone_area_code: contact.phone_area_code || '',
        mobile_country_code: contact.mobile_country_code || '',
        mobile_area_code: contact.mobile_area_code || '',
        is_customer: ct === 'CUSTOMER' || ct === 'BOTH' || !ct,
        is_supplier: ct === 'SUPPLIER' || ct === 'BOTH',
        contact_status: contact.contact_status || 'ACTIVE'
      });
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  // Suggested account number (prefix logic moved to Contact Groups screen)
  const getSuggestedAccountNoWithPrefix = () => suggestedAccountNo;

  /*
   * A Customer / Supplier tick that Xero holds, on a contact saved with it,
   * is locked on: unticking it would be refused by the server, and if it were
   * not, the next sync would tick it again. Shown disabled with the reason
   * (never hidden). A flag Xero holds that the saved type lacks is left for
   * the user — the server adds it on save, as the sync would.
   */
  const savedType = String(contact?.contact_type || '').toUpperCase();
  const typeLock = {
    customer: xeroTypeFlags.customer === true && (savedType === 'CUSTOMER' || savedType === 'BOTH'),
    supplier: xeroTypeFlags.supplier === true && (savedType === 'SUPPLIER' || savedType === 'BOTH')
  };
  const typeLockReason = (kind) => (kind === 'customer'
    ? 'Xero has this contact as a customer (it has sales invoices in Xero), so it can\'t be unticked here.'
    : 'Xero has this contact as a supplier (it has bills in Xero), so it can\'t be unticked here.');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: { ...prev[addressType], [field]: value }
    }));
    setError('');
  };

  const validateAndSubmit = async (skipGroupChecks = false) => {
    const editing = !!(contact && contact.id);

    /*
     * Which groups will this contact be in? The multiselect, in both modes —
     * it is editable on edit too, and reading the contact's ORIGINAL
     * membership here meant the Email Invoices prompt fired off what the
     * contact used to be in rather than what the user had just picked.
     *
     * Falls back to the loaded membership only before detail has arrived,
     * when the multiselect is still empty for want of loading.
     */
    const groupList = (editing && !detailLoaded)
      ? (contact.groupsArray || (contact.groups ? contact.groups.split(',').map(g => g.trim()) : []))
      : selectedGroups;
    const inEmailInvoicesGroup = groupList.some(g => g && g.toLowerCase() === 'email invoices');

    // Email Invoices contacts should carry an "E" account-number prefix and an
    // email address. Prompt for whichever is missing — the prefix only on create,
    // where the account number is still editable.
    if (!skipGroupChecks && inEmailInvoicesGroup) {
      const effectiveAccountNo = (formData.account_no && formData.account_no.trim())
        ? formData.account_no.trim()
        : suggestedAccountNo;
      const needsPrefix = !editing && !!effectiveAccountNo && !/^E/i.test(effectiveAccountNo);
      const needsEmail = !formData.email;
      if (needsPrefix || needsEmail) {
        setGroupSetupPrompt({ needsEmail, needsPrefix });
        setPendingSubmit(true);
        return;
      }
    }

    await performSubmit();
  };

  // "Add …" on the Email Invoices prompt: auto-apply the E prefix (mechanical),
  // then return to the form so the user can add the email address themselves.
  const applyEmailInvoicesDefaults = (needsPrefix) => {
    setGroupSetupPrompt(null);
    setPendingSubmit(false);
    if (needsPrefix) {
      const base = (formData.account_no && formData.account_no.trim())
        ? formData.account_no.trim()
        : suggestedAccountNo;
      if (base && !/^E/i.test(base)) {
        handleChange('account_no', `E${base}`);
      } else if (!formData.account_no && suggestedAccountNo) {
        handleChange('account_no', `E${suggestedAccountNo}`);
      }
    }
  };

  /**
   * Ask the server whether a just-created account belongs under Nutri-Ag.
   *
   * The rule — which spellings count, and which account number is the parent —
   * lives on the server, so this knows nothing except how to ask. Any failure
   * answers "no": the contact has already been saved at this point, and a
   * broken suggestion must not make a successful save look like a failure.
   */
  const fetchNutriAgSuggestion = async (contactId) => {
    try {
      const res = await fetch(`${API_URL}/api/contacts/${contactId}/nutri-ag-suggestion`,
        { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.suggest ? data : null;
    } catch {
      return null;
    }
  };

  /** Accept the suggestion: link as a pricing-only child of the Nutri-Ag parent. */
  const linkToNutriAg = async () => {
    const { contact: child, parent } = nutriAg;
    setNutriAgLinking(true);
    try {
      const res = await fetch(`${API_URL}/api/contacts/joint-accounts/${parent.id}/children`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          childIds: [child.id],
          // Pricing only — the scope every other child of this parent carries.
          // Spelled out rather than left to the endpoint's defaults, which turn
          // statements and reports ON.
          scopes: { statements: false, reports: false, pricing: true, credit: false }
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not link the account');
      finishAfterNutriAg();
    } catch (err) {
      // The contact itself saved fine; only the link failed. Say exactly that,
      // rather than leaving the impression the whole save came apart.
      setNutriAg(null);
      setNutriAgLinking(false);
      setXeroWarning(`${child.company_name} was created, but could not be added to `
        + `${parent.company_name}: ${err.message}. You can link it from the contact's Joint Account section.`);
    }
  };

  /** Whichever way the prompt was answered, resume the normal close path. */
  const finishAfterNutriAg = () => {
    const warning = nutriAg?.xeroWarning;
    setNutriAg(null);
    setNutriAgLinking(false);
    if (warning) setXeroWarning(warning); else handleClose();
  };

  const performSubmit = async (resolvedConflicts = null, xeroValues = null) => {
    const isEditing = contact && contact.id;
    const hasXeroId = isEditing && contact.xero_contact_id;

    // If editing a Xero contact with no prior conflict resolution, check for conflicts first
    if (isEditing && hasXeroId && !resolvedConflicts) {
      try {
        const checkRes = await fetch(
          `${API_URL}/api/contacts/${contact.id}/conflict-check?since=${encodeURIComponent(contact.xero_modified_date || '')}`,
          { credentials: 'include' }
        );
        const checkData = await checkRes.json();

        if (checkData.hasConflict && checkData.conflicts?.length > 0) {
          const userChanged = new Set();
          if (initialFormData) {
            const normalize = (v) => (v === null || v === undefined || v === '') ? '' : String(v);
            for (const key of Object.keys(initialFormData)) {
              if (normalize(formData[key]) !== normalize(initialFormData[key])) {
                userChanged.add(key);
              }
            }
          }
          setConflictData({ ...checkData, userChangedFields: userChanged });
          setShowConflictModal(true);
          return;
        }
      } catch (err) {
        console.warn('Conflict check failed, proceeding with save:', err.message);
      }
    }

    setLoading(true);
    setError('');

    try {
      const url = isEditing
        ? `${API_URL}/api/contacts/${contact.id}`
        : `${API_URL}/api/contacts`;

      let contact_type = 'CUSTOMER';
      if (formData.is_customer && formData.is_supplier) {
        contact_type = 'BOTH';
      } else if (formData.is_supplier && !formData.is_customer) {
        contact_type = 'SUPPLIER';
      }

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          contact_type,
          createInXero: !isEditing,
          updateInXero: isEditing,
          // Contact groups: on create send the picked set; on edit send only once
          // the contact's detail has loaded, so a stale/partial prop can't wipe
          // the membership the backend replaces wholesale.
          ...(!isEditing ? { groups: selectedGroups } : (detailLoaded ? { groups: selectedGroups } : {})),
          // People (Xero ContactPersons), bundled with the save. The full
          // non-primary set replaces the contact's people in Xero; rows without an
          // email are dropped (Xero requires one). Plus the primary person's name
          // (its email is the Email field above). Only when editing + detail loaded.
          /*
           * People go with the save on create as well as edit. On edit they wait
           * for detailLoaded, because sending a partial set would replace the
           * contact's people in Xero with it; on create there is nothing to
           * replace, so the set in hand is the whole truth.
           */
          ...(!isEditing || detailLoaded
            ? {
                contactPersons: contactPersons
                  .filter(p => !p.is_primary && (p.email || '').trim())
                  .map(p => ({
                    first_name: (p.first_name || '').trim(),
                    last_name: (p.last_name || '').trim(),
                    email: (p.email || '').trim(),
                    include_in_emails: !!p.include_in_emails,
                    include_in_po_emails: !!p.include_in_po_emails
                  })),
                primary_first_name: (contactPersons.find(p => p.is_primary)?.first_name || '').trim(),
                primary_last_name: (contactPersons.find(p => p.is_primary)?.last_name || '').trim()
              }
            : {}),
          // VAT: send only when editing with detail loaded (else undefined → the
          // backend leaves it unchanged), or on create.
          ...(isEditing && !detailLoaded ? { tax_number: undefined } : {}),
          // The till drafts-for-pricing tick likewise: never from a partial
          // prop, and only once its column exists.
          ...((isEditing && !detailLoaded) || !tillDraftReady ? { till_draft_pricing: undefined } : {}),
          ...(resolvedConflicts && { resolvedConflicts, xeroValues })
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.xeroLimitation && data.message) {
          throw new Error(data.message);
        }
        throw new Error(data.error || `Failed to ${isEditing ? 'update' : 'create'} contact`);
      }

      if (data.success) {
        // Second arg is the persisted contact record (with id/account_no) so
        // callers that create-then-link (e.g. Sync Contacts) can map it.
        onSave(data.createdInXero || data.updatedInXero, data.contact || null);

        // A new Nutri-Ag account almost certainly prices off Nutri-Ag LTD, and
        // the moment to say so is now — while the person who knows why they
        // created it is still looking at it. Asked, never assumed: the rule is
        // reliable enough to raise and not reliable enough to apply silently.
        // Only on create; changing an existing account's group is a decision
        // with consequences for the group it is already in.
        const suggestion = !isEditing && data.contact?.id
          ? await fetchNutriAgSuggestion(data.contact.id)
          : null;

        if (suggestion) {
          setNutriAg({ ...suggestion, xeroWarning: data.xeroWarning || '' });
        } else if (data.xeroWarning) {
          setXeroWarning(data.xeroWarning);
        } else {
          handleClose();
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /*
   * What a contact must have before it can be saved.
   *
   * These are the things that quietly break something downstream when they
   * are missing, rather than house rules: an account with no number cannot be
   * matched on a statement run or a bank feed, and a customer in neither
   * invoice group is one nothing will ever invoice.
   *
   * Returns a message, or null when the contact is good to save.
   */
  const isEditing = !!(contact && contact.id);

  const validationError = () => {
    if (!formData.is_customer && !formData.is_supplier) {
      return 'Please select at least one contact type (Customer or Supplier).';
    }

    /*
     * Account number. Required on CREATE only — 1,418 of the 2,247 accounts
     * on file have none, and requiring one on edit would block every
     * unrelated change to any of them until somebody invented a number.
     * The suggested number counts: leaving the field blank accepts it.
     */
    if (!isEditing) {
      const effective = (formData.account_no || '').trim() || suggestedAccountNo;
      if (!effective) {
        return 'An account number is required. Type one, or use the suggested number.';
      }
    }

    /*
     * A customer has to be invoiced somehow. Postal Invoices and Email
     * Invoices are how the invoice and statement runs find them, so a
     * customer in neither is a customer nothing will ever send anything to.
     *
     * On edit as well as create: the Contact Groups multiselect above is
     * editable in both modes, so this is a rule somebody can satisfy from
     * where they are standing. Only 61 of 1,179 customers are in neither
     * group — 23 of them active — so it asks almost nobody for anything, and
     * where it does ask, that is the data problem it exists to catch.
     *
     * Waits for detailLoaded when editing: until the contact's own membership
     * has arrived, selectedGroups is empty because nothing has loaded, not
     * because the contact is in no groups.
     */
    if (formData.is_customer && (!isEditing || detailLoaded)) {
      const inInvoiceGroup = selectedGroups.some(g =>
        ['postal invoices', 'email invoices'].includes(String(g || '').trim().toLowerCase()));
      if (!inInvoiceGroup) {
        return 'A customer must be in either the Postal Invoices or the Email Invoices group — otherwise nothing will ever invoice them. Add one under Contact Groups.';
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const problem = validationError();
    if (problem) {
      setError(problem);
      setLoading(false);
      return;
    }

    setLoading(false);
    await validateAndSubmit();
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
      onClose();
    }, 300);
  };

  const copyDeliveryToInvoice = () => {
    setFormData(prev => ({ ...prev, invoiceAddress: { ...prev.deliveryAddress } }));
  };

  const suggestedWithPrefix = getSuggestedAccountNoWithPrefix();

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content contact-edit-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel-header">
          <div className="header-title">
            <h2>{contact ? 'Edit Contact' : 'Add Contact'}</h2>
          </div>
          <button className="btn-icon close-btn" onClick={handleClose} title="Close">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <div className="panel-body">
          <form id="contact-edit-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-banner">
                {error}
              </div>
            )}

            {isEditing && (xeroPending.pending || xeroPending.note) && (
              <div className="xero-pending-banner" role="status">
                <span className="material-symbols-rounded">cloud_sync</span>
                <span>
                  {xeroPending.pending
                    ? 'Not yet in Xero — saved here while Xero was not answering. It will be added to Xero automatically, with its addresses, people and groups.'
                    : 'In Xero, with a note from when it was sent:'}
                  {xeroPending.note && <span className="xero-pending-note">{xeroPending.pending ? ` Last attempt: ${xeroPending.note}` : ` ${xeroPending.note}`}</span>}
                </span>
              </div>
            )}

            <div className="form-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 'var(--spacing-md)'}}>

              {/* --- Account Details Section --- */}
              <div className="form-section-heading" style={{gridColumn: 'span 8'}}>Account Details</div>

              <div className="form-field" style={{gridColumn: 'span 1'}}>
                <label>Acc #</label>
                <Input
                  type="text"
                  value={formData.account_no}
                  onChange={(e) => handleChange('account_no', e.target.value)}
                  placeholder={suggestedWithPrefix || 'Auto'}
                  title="Must be unique. Saving pushes the change to Xero."
                />
                {isEditing && contact.xero_contact_id && formData.account_no && (
                  <span style={{position: 'absolute', zIndex: '10', top: '64px', fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>
                    Syncs to Xero
                  </span>
                )}
                {suggestedWithPrefix && !formData.account_no && (
                  <button
                    type="button"
                    onClick={() => handleChange('account_no', suggestedWithPrefix)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-primary)',
                      fontSize: 'var(--font-xs)',
                      cursor: 'pointer',
                      padding: '2px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: '12px' }}>add</span>
                    Use {suggestedWithPrefix}
                  </button>
                )}
              </div>

              <div className="form-field" style={{gridColumn: 'span 4'}}>
                <label>Name *</label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  required
                  placeholder="Enter name..."
                />
              </div>

              <div className="form-field" style={{gridColumn: 'span 3'}}>
                <label>Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter email address..."
                />
              </div>

              <div className="form-field" style={{gridColumn: 'span 3'}}>
                <label>VAT Number</label>
                <Input
                  type="text"
                  value={formData.tax_number}
                  onChange={(e) => handleChange('tax_number', e.target.value)}
                  placeholder="e.g. GB123456789"
                />
              </div>

              {/* Local only, never sent to Xero: this customer's book-to-account
                  till sales holding a product in 200C/200M/200N/207/207A go to
                  Xero as DRAFT invoices and are priced in Pricing & Costing. A
                  customer's tick — disabled, not hidden, for a supplier. */}
              <div className="form-field till-draft-field" style={{ gridColumn: 'span 5' }}>
                <label>Till account sales</label>
                <TickCheckbox
                  id="till-draft-pricing-check"
                  checked={formData.till_draft_pricing && formData.is_customer}
                  onChange={(e) => handleChange('till_draft_pricing', e.target.checked)}
                  disabled={!formData.is_customer || !tillDraftReady}
                  title={!formData.is_customer
                    ? 'Only a customer\'s till sales are invoiced'
                    : !tillDraftReady
                      ? 'Needs a database update first (draft_invoices_for_pricing)'
                      : 'Book-to-account till sales with a product in 200C, 200M, 200N, 207 or 207A go to Xero as draft invoices, priced in Pricing & Costing. Kept here only — not sent to Xero.'}
                  label="Book-to-account sales in 200C/200M/200N/207/207A go to Xero as drafts for pricing"
                />
              </div>

              {/* --- Phone Numbers Section --- */}
              <div className="form-section-heading" style={{gridColumn: 'span 8'}}>Phone Numbers</div>

              <div className="form-field" style={{gridColumn: 'span 3'}}>
                <label>Phone</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Input
                    type="text"
                    value={formData.phone_country_code}
                    onChange={(e) => handleChange('phone_country_code', e.target.value)}
                    placeholder="Country"
                    style={{ flex: '0 0 60px' }}
                  />
                  <Input
                    type="text"
                    value={formData.phone_area_code}
                    onChange={(e) => handleChange('phone_area_code', e.target.value)}
                    placeholder="Area"
                    style={{ flex: '0 0 60px' }}
                  />
                  <Input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Number..."
                    style={{ flex: '1' }}
                  />
                </div>
              </div>

              <div className="form-field" style={{gridColumn: 'span 3'}}>
                <label>Mobile</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Input
                    type="text"
                    value={formData.mobile_country_code}
                    onChange={(e) => handleChange('mobile_country_code', e.target.value)}
                    placeholder="Country"
                    style={{ flex: '0 0 60px' }}
                  />
                  <Input
                    type="text"
                    value={formData.mobile_area_code}
                    onChange={(e) => handleChange('mobile_area_code', e.target.value)}
                    placeholder="Area"
                    style={{ flex: '0 0 60px' }}
                  />
                  <Input
                    type="text"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    placeholder="Number..."
                    style={{ flex: '1' }}
                  />
                </div>
              </div>

              <div className="form-field" style={{gridColumn: 'span 2'}}>
                <label>Sales Rep</label>
                <Dropdown
                  value={formData.rep_id}
                  onChange={(value) => handleChange('rep_id', value)}
                  options={[
                    { value: '', label: 'Select sales rep...' },
                    ...salesRepOptions.map(rep => ({ value: rep.id, label: rep.name }))
                  ]}
                  placeholder="Select sales rep..."
                />
              </div>

              {/* Status */}
              <div className="form-field" style={{gridColumn: 'span 2'}}>
                <label style={{ top: '5px' }}>Status</label>
                <Dropdown
                  value={formData.contact_status}
                  onChange={(value) => handleChange('contact_status', value)}
                  options={[
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'ARCHIVED', label: 'Archived' }
                  ]}
                />
              </div>

              {/* Contact Groups — editable multiselect (checkbox list) on both
                  create and edit. On edit we only send the set once the contact's
                  detail has loaded (detailLoaded), so a partial prop can't wipe
                  membership. */}
              <div className="form-field" style={{ gridColumn: 'span 4' }}>
                <label style={{ top: '5px' }}>Contact Groups</label>
                <MultiSelectDropdown
                  options={availableGroups.map(g => ({ value: g.name, label: g.name, searchText: g.name }))}
                  value={selectedGroups}
                  onChange={setSelectedGroups}
                  placeholder={isEditing && !detailLoaded ? 'Loading groups…' : 'Add to groups...'}
                  emptyMessage="No groups available"
                  disabled={isEditing && !detailLoaded}
                  hideTags
                />
              </div>

              {/* Contact Type — at least one of Customer / Supplier is required */}
              <div className="form-field" style={{gridColumn: 'span 2' }}>
                <label style={{ top: '5px' }}>Type <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr', gap: 'var(--spacing-md)', justifyItems: 'space-between' }}>
                  <label style={{ top: '5px' }} className="tick-check-label"
                    title={typeLock.customer ? typeLockReason('customer') : undefined}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="is-customer-check"
                      checked={formData.is_customer}
                      disabled={typeLock.customer}
                      onChange={(e) => handleChange('is_customer', e.target.checked)}
                    />
                    <label htmlFor="is-customer-check" className="tick-check">
                      <svg width="18px" height="18px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                    <span style={{ marginBottom: '5px' }}>Customer</span>
                  </label>
                  <label style={{ top: '5px' }} className="tick-check-label"
                    title={typeLock.supplier ? typeLockReason('supplier') : undefined}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="is-supplier-check"
                      checked={formData.is_supplier}
                      disabled={typeLock.supplier}
                      onChange={(e) => handleChange('is_supplier', e.target.checked)}
                    />
                    <label htmlFor="is-supplier-check" className="tick-check">
                      <svg width="18px" height="18px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                    <span style={{ marginBottom: '5px' }}>Supplier</span>
                  </label>
                </div>
                {(typeLock.customer || typeLock.supplier) && (
                  <div className="contact-type-lock-note">
                    <span className="material-symbols-rounded">lock</span>
                    <span>
                      {typeLock.customer && typeLock.supplier
                        ? 'Xero has this contact as both a customer and a supplier (from its invoices and bills), so neither tick can be removed here.'
                        : typeLockReason(typeLock.customer ? 'customer' : 'supplier')}
                    </span>
                  </div>
                )}
              </div>

              {/* Haulage rates are not a field of the contact, so they are not
                  here — said so, with the icon to look for, rather than leaving
                  someone hunting through this form for them. */}
              {formData.is_supplier && (
                <div className="supplier-haulage-note" style={{ gridColumn: 'span 8' }}>
                  <span className="material-symbols-rounded">local_shipping</span>
                  <span>
                    Haulage rates for this supplier aren't edited here. Close this and, in the
                    contact's view window, open <strong>Haulage rates</strong> from the icons down
                    the right-hand side (<span className="material-symbols-rounded inline-icon">local_shipping</span>).
                  </span>
                </div>
              )}

              {/* --- Credit Section --- */}
              <div className="form-section-heading" style={{gridColumn: 'span 8'}}>Credit Control</div>

              <div className="form-field" style={{gridColumn: 'span 2'}}>
                <label>Credit Limit</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.credit_limit}
                  onChange={(e) => handleChange('credit_limit', e.target.value)}
                  placeholder="Set limit..."
                />
              </div>

              <div className="form-field" style={{gridColumn: 'span 4'}}>
                <label>Credit Limit Notes</label>
                <Input
                  type="text"
                  value={formData.credit_limit_notes}
                  onChange={(e) => handleChange('credit_limit_notes', e.target.value)}
                  placeholder="Enter note..."
                />
              </div>

              {/* Flags row — Contracted + No Statement share their own tidy row */}
              <div className="form-field" style={{gridColumn: 'span 1', display: 'flex', alignItems: 'flex-start' }}>
                <label className="tick-check-label">
                  <input
                    type="checkbox"
                    className="tick-check-input"
                    id="is-contracted-check"
                    checked={formData.contracted}
                    onChange={(e) => handleChange('contracted', e.target.checked)}
                  />
                  <label htmlFor="is-contracted-check" className="tick-check">
                    <svg width="18px" height="18px" viewBox="0 0 18 18">
                      <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                      <polyline points="1 9 7 14 15 4"></polyline>
                    </svg>
                  </label>
                  <span style={{ marginBottom: '5px' }}>Contracted</span>
                </label>
              </div>

              {/* Statement — local-only flag, stored inverted as no_statement
                  (excluded from the "No invoice group" data-check). The checkbox
                  reads positively: ticked = customer receives a statement. */}
              <div className="form-field" style={{gridColumn: 'span 1', display: 'flex', alignItems: 'flex-start' }}>
                <label className="tick-check-label">
                  <input
                    type="checkbox"
                    className="tick-check-input"
                    id="no-statement-check"
                    checked={!formData.no_statement}
                    onChange={(e) => handleChange('no_statement', !e.target.checked)}
                  />
                  <label htmlFor="no-statement-check" className="tick-check">
                    <svg width="18px" height="18px" viewBox="0 0 18 18">
                      <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                      <polyline points="1 9 7 14 15 4"></polyline>
                    </svg>
                  </label>
                  <span style={{ marginBottom: '5px' }}>Statement</span>
                </label>
              </div>

              {/* On by default, and the warning is for turning it OFF on a
                  customer. Somebody who always pays at the till looks like a
                  contact that needs no statement — right up until the day
                  they book something to the account, and then nothing ever
                  chases it. Said here rather than blocked: there are
                  customers who genuinely should not get one. Its own full-width
                  row of the form grid, not squeezed into the checkbox's cell. */}
              {formData.no_statement && formData.is_customer && (
                <p className="statement-off-warning">
                  <span className="material-symbols-rounded">warning</span>
                  <span>
                    Statements will never be generated for this customer — including
                    for anything they later book to their account rather than paying
                    for at the till.
                  </span>
                </p>
              )}

              {/* --- People --- Add / edit / remove the contact's people (Xero
                  ContactPersons): first + last name, email, and whether they
                  receive statement emails. Each existing row is read-only with an
                  Edit button; the primary person is Xero's top-level email (email
                  fixed to the Email field above, name editable). Bundled into the
                  save + pushed to Xero. */}
              <div className="form-section-heading" style={{ gridColumn: 'span 8' }}>People</div>
                  <div className="form-field contact-persons-edit" style={{ gridColumn: 'span 8' }}>
                    <p className="contact-persons-hint">
                      People on this contact. Tick "gets statements" for each address that should receive statement emails. Saved (and synced to Xero) with the rest of your changes.
                    </p>
                    <div className="contact-persons-list">
                      {contactPersons.map((person, idx) => {
                        const editing = !!person._editing;
                        const fullName = [person.first_name, person.last_name].filter(Boolean).join(' ');
                        return (
                          <div key={idx} className={`contact-person-item ${editing ? 'editing' : 'readonly'}`}>
                            {editing ? (
                              <>
                                <Input type="text" value={person.first_name || ''} onChange={(e) => updatePerson(idx, 'first_name', e.target.value)} placeholder="First name" />
                                <Input type="text" value={person.last_name || ''} onChange={(e) => updatePerson(idx, 'last_name', e.target.value)} placeholder="Last name" />
                                {person.is_primary ? (
                                  <span className="person-email fixed-email" title="Primary email — edit it in the Email field above">{formData.email || '—'}</span>
                                ) : (
                                  <Input type="email" value={person.email || ''} onChange={(e) => updatePerson(idx, 'email', e.target.value)} placeholder="Email" />
                                )}
                              </>
                            ) : (
                              <>
                                {/* Star beside the name it belongs to, rather
                                    than floating at the end of the row where it
                                    read as a property of the whole line. */}
                                <span className="person-name">
                                  {fullName || (person.is_primary ? 'Primary contact' : 'Unnamed')}
                                  {person.is_primary && (
                                    <span className="person-badge primary" title="Primary contact">
                                      <span className="material-symbols-rounded">star</span>
                                    </span>
                                  )}
                                </span>
                                <span className="person-email">{person.is_primary ? (formData.email || person.email || '—') : (person.email || '—')}</span>
                              </>
                            )}

                            <span className="person-email-flag" title={
                              person.is_primary
                                ? 'Primary email — always included in Xero emails'
                                : (person.include_in_emails ? 'Included in statement emails' : 'Not included in statement emails')
                            }>
                              <span className="flag-caption">Statements</span>
                              <TickCheckbox
                                id={`edit-email-flag-${idx}`}
                                checked={person.is_primary ? true : !!person.include_in_emails}
                                disabled={person.is_primary}
                                className={person.is_primary ? 'primary-locked' : ''}
                                onChange={person.is_primary ? undefined : () => updatePerson(idx, 'include_in_emails', !person.include_in_emails)}
                                ariaLabel="Include this address in statement emails"
                              />
                            </span>

                            {/* Purchase orders, not statements. Statements go
                                to customers and orders go to suppliers, so the
                                right people are rarely the same — and unlike
                                the flag above, the PRIMARY person is not
                                automatically included: an order desk is a
                                different mailbox from whoever the account is
                                in the name of.

                                Only on a supplier. Read off the live form flag
                                rather than the saved contact_type, so ticking
                                Supplier reveals it without a save first. */}
                            {formData.is_supplier && (
                            <span className="person-email-flag" title={
                              person.include_in_po_emails
                                ? 'Included in purchase order emails to this supplier'
                                : 'Not included in purchase order emails'
                            }>
                              <span className="flag-caption">POs</span>
                              <TickCheckbox
                                id={`edit-po-flag-${idx}`}
                                checked={!!person.include_in_po_emails}
                                onChange={() => updatePerson(idx, 'include_in_po_emails', !person.include_in_po_emails)}
                                ariaLabel="Include this address in purchase order emails"
                              />
                            </span>
                            )}

                            <div className="person-row-actions">
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => setPersonEditing(idx, !editing)}
                                title={editing ? 'Done' : 'Edit'}
                              >
                                <span className="material-symbols-rounded">{editing ? 'check' : 'edit'}</span>
                              </button>
                              {!person.is_primary && (
                                <button
                                  type="button"
                                  className="btn-icon btn-danger remove-person"
                                  onClick={() => removePerson(idx)}
                                  title="Remove this person"
                                >
                                  <span className="material-symbols-rounded">delete</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="add-person-row">
                      <button type="button" className="btn-slideout" onClick={addPerson}>
                        <div className="icon-wrapper"><span className="material-symbols-rounded">person_add</span></div>
                        <span className="btn-label">Add person</span>
                      </button>
                    </div>
                  </div>

              {/* --- Addresses Section --- */}
              <div className="form-section-heading" style={{gridColumn: 'span 8'}}>Addresses</div>

              <div className="form-field" style={{ gridColumn: 'span 4' }}>
                <label>Delivery Address</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Input type="text" value={formData.deliveryAddress.attention} onChange={(e) => handleAddressChange('deliveryAddress', 'attention', e.target.value)} placeholder="Attention" />
                  <Input type="text" value={formData.deliveryAddress.line1} onChange={(e) => handleAddressChange('deliveryAddress', 'line1', e.target.value)} placeholder="Address Line 1" />
                  <Input type="text" value={formData.deliveryAddress.line2} onChange={(e) => handleAddressChange('deliveryAddress', 'line2', e.target.value)} placeholder="Address Line 2" />
                  <Input type="text" value={formData.deliveryAddress.line3} onChange={(e) => handleAddressChange('deliveryAddress', 'line3', e.target.value)} placeholder="Address Line 3" />
                  <Input type="text" value={formData.deliveryAddress.line4} onChange={(e) => handleAddressChange('deliveryAddress', 'line4', e.target.value)} placeholder="Address Line 4" />
                  <Input type="text" value={formData.deliveryAddress.city} onChange={(e) => handleAddressChange('deliveryAddress', 'city', e.target.value)} placeholder="Town / City" />
                  <Input type="text" value={formData.deliveryAddress.region} onChange={(e) => handleAddressChange('deliveryAddress', 'region', e.target.value)} placeholder="County" />
                  <Input type="text" value={formData.deliveryAddress.postalCode} onChange={(e) => handleAddressChange('deliveryAddress', 'postalCode', e.target.value)} placeholder="Postcode" />
                  <Input type="text" value={formData.deliveryAddress.country} onChange={(e) => handleAddressChange('deliveryAddress', 'country', e.target.value)} placeholder="Country" />
                </div>
              </div>

              <div className="form-field" style={{ gridColumn: 'span 4' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Invoice Address</label>
                  {formData.deliveryAddress.line1 && (
                    <button
                      type="button"
                      onClick={copyDeliveryToInvoice}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-primary)',
                        fontSize: 'var(--font-xs)',
                        cursor: 'pointer',
                        display: 'flex',
                        paddingBottom: 'var(--spacing-md)',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>content_copy</span>
                      Copy from delivery
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Input type="text" value={formData.invoiceAddress.attention} onChange={(e) => handleAddressChange('invoiceAddress', 'attention', e.target.value)} placeholder="Attention" />
                  <Input type="text" value={formData.invoiceAddress.line1} onChange={(e) => handleAddressChange('invoiceAddress', 'line1', e.target.value)} placeholder="Address Line 1" />
                  <Input type="text" value={formData.invoiceAddress.line2} onChange={(e) => handleAddressChange('invoiceAddress', 'line2', e.target.value)} placeholder="Address Line 2" />
                  <Input type="text" value={formData.invoiceAddress.line3} onChange={(e) => handleAddressChange('invoiceAddress', 'line3', e.target.value)} placeholder="Address Line 3" />
                  <Input type="text" value={formData.invoiceAddress.line4} onChange={(e) => handleAddressChange('invoiceAddress', 'line4', e.target.value)} placeholder="Address Line 4" />
                  <Input type="text" value={formData.invoiceAddress.city} onChange={(e) => handleAddressChange('invoiceAddress', 'city', e.target.value)} placeholder="Town / City" />
                  <Input type="text" value={formData.invoiceAddress.region} onChange={(e) => handleAddressChange('invoiceAddress', 'region', e.target.value)} placeholder="County" />
                  <Input type="text" value={formData.invoiceAddress.postalCode} onChange={(e) => handleAddressChange('invoiceAddress', 'postalCode', e.target.value)} placeholder="Postcode" />
                  <Input type="text" value={formData.invoiceAddress.country} onChange={(e) => handleAddressChange('invoiceAddress', 'country', e.target.value)} placeholder="Country" />
                </div>
              </div>

              {/* --- Notes Section --- */}
              <div className="form-section-heading" style={{gridColumn: 'span 8'}}>Notes</div>

              {/* What will physically get in: "Artic", "8 Wheeler", "No HGV
                  access", or a haulier's name where only they will try it.
                  Internal — it reaches drivers, never customers or suppliers. */}
              <div className="form-field" style={{ gridColumn: 'span 8' }}>
                <label>Largest Vehicle Access</label>
                <Input
                  type="text"
                  value={formData.largest_vehicle_access}
                  onChange={(e) => handleChange('largest_vehicle_access', e.target.value)}
                  placeholder="e.g. Artic, 8 Wheeler, Rear Steer, No HGV access"
                />
              </div>

              <div className="form-field" style={{ gridColumn: 'span 8' }}>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows="4"
                  placeholder="Enter any notes about this contact..."
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>

            </div>

          </form>
        </div>

        <div className="panel-footer">
          <button data-write-action
            type="submit"
            form="contact-edit-form"
            className="btn-slideout"
            disabled={loading}
            onClick={handleSubmit}
          >
            <div className="icon-wrapper">
              <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
                {loading ? 'sync' : 'save'}
              </span>
            </div>
            <span className="btn-label">{loading ? 'Saving...' : (contact ? 'Save Changes' : 'Add Contact')}</span>
          </button>
          <button data-read-safe
            type="button"
            onClick={handleClose}
            className="btn-round btn-danger"
            disabled={loading}
          >
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">Cancel</span>
          </button>
        </div>
      </div>

      {/* Email Invoices setup prompt — recommend an E prefix + email address */}
      {groupSetupPrompt && (
        <div className="modal-overlay" style={{ zIndex: 1001 }} onClick={() => { setGroupSetupPrompt(null); setPendingSubmit(false); }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '460px', padding: '24px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span className="material-symbols-rounded" style={{ fontSize: '32px', color: 'var(--color-warning)' }}>
                mail
              </span>
              <h3 style={{ margin: 0 }}>Email Invoices contact</h3>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.6' }}>
              This contact will be added to the <strong>Email Invoices</strong> group. These contacts should have:
            </p>
            <ul style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.7', paddingLeft: '20px' }}>
              {groupSetupPrompt.needsPrefix && <li>an <strong>“E”</strong> account-number prefix</li>}
              {groupSetupPrompt.needsEmail && <li>an <strong>email address</strong></li>}
            </ul>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              borderTop: '1px solid var(--border-primary)',
              paddingTop: '16px'
            }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  setGroupSetupPrompt(null);
                  if (pendingSubmit) {
                    validateAndSubmit(true);
                  }
                }}
              >
                Continue anyway
              </button>
              <button
                className="btn-primary"
                onClick={() => applyEmailInvoicesDefaults(groupSetupPrompt.needsPrefix)}
              >
                {groupSetupPrompt.needsPrefix ? 'Add E prefix' : 'Add details'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => { setShowConflictModal(false); setConflictData(null); }}
        onResolve={({ resolutions, xeroValues }) => {
          setShowConflictModal(false);
          setConflictData(null);
          performSubmit(resolutions, xeroValues);
        }}
        entityType="contact"
        conflicts={conflictData?.conflicts || []}
        userChangedFields={conflictData?.userChangedFields || new Set()}
        xeroUpdatedAt={conflictData?.xeroUpdatedAt}
      />

      <AlertDialog
        isOpen={!!xeroWarning}
        type="warning"
        message={xeroWarning}
        onClose={() => { setXeroWarning(''); handleClose(); }}
      />

      {/* The contact is already saved. This asks about ONE further thing, and
          says which of the name or the rep prompted it so the answer can be a
          judgement rather than a guess. */}
      <ConfirmDialog
        isOpen={!!nutriAg}
        type="info"
        title="Add to the Nutri-Ag group?"
        message={nutriAg
          ? `${nutriAg.contact.company_name} has ${
              nutriAg.matchedOn.includes('name') && nutriAg.matchedOn.includes('rep')
                ? 'Nutri-Ag in its name and as its rep'
                : nutriAg.matchedOn.includes('rep') ? 'Nutri-Ag as its rep' : 'Nutri-Ag in its name'
            }. Add it as a child of ${nutriAg.parent.company_name} (${nutriAg.parent.account_no}) `
            + 'so it prices off the Nutri-Ag contracts?\n\nPricing only — statements, reports and '
            + 'credit stay with this account.'
          : ''}
        confirmText={nutriAgLinking ? 'Adding...' : 'Add to group'}
        cancelText="Leave separate"
        onConfirm={() => { if (!nutriAgLinking) linkToNutriAg(); }}
        onCancel={() => { if (!nutriAgLinking) finishAfterNutriAg(); }}
      />
    </div>,
    document.body
  );
}

export default ContactEditModal;
